/**
 * Browser client for the parent portal (#268). Families have no Supabase session:
 * everything goes through /api/parent-auth and /api/parent with the app-scoped
 * httpOnly session cookie (same-origin, so `credentials: 'same-origin'`).
 */

export class ParentApiError extends Error {
  status: number;
  reason?: string;
  constructor(message: string, status: number, reason?: string) {
    super(message);
    this.status = status;
    this.reason = reason;
  }
}

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      credentials: 'same-origin',
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    });
  } catch {
    throw new ParentApiError('We could not reach the school. Check your internet connection and try again.', 0);
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ParentApiError(body.error || 'Something went wrong. Please try again.', response.status, body.reason);
  }
  return body as T;
}

const post = <T>(url: string, body: unknown) => call<T>(url, { method: 'POST', body: JSON.stringify(body) });

export type ParentSessionInfo = { email: string; expiresAt: string };

export const parentAuth = {
  requestCode: (email: string) => post<{ ok: true; expiresInMinutes: number }>('/api/parent-auth', { action: 'request-code', email }),
  verify: (email: string, code: string) => post<{ ok: true; email: string }>('/api/parent-auth', { action: 'verify', email, code }),
  session: () => call<ParentSessionInfo>('/api/parent-auth?action=session'),
  logout: () => post<{ ok: true }>('/api/parent-auth', { action: 'logout' }),
};

export type ParentDocumentSummary = {
  id: string;
  document_title: string | null;
  document_type: string | null;
  document_period: string | null;
  status: string;
  student_id: string;
  student_name: string | null;
  sent_at: string | null;
  signed_at: string | null;
  viewed_at: string | null;
  updated_at: string | null;
};

export type ParentStudentDetails = {
  id: string;
  name: string | null;
  student_id: string | null;
  year_level: string | null;
  date_of_birth: string | null;
  house: string | null;
  individual_diagnosis: string | null;
  nccd_category: string | null;
};

export type ParentDocumentPayload = {
  // The document row with internal fields, tenant_id and created_by removed server-side.
  document: Record<string, any>;
  student: ParentStudentDetails | null;
  fieldOrder: string[];
  fieldLabels: Record<string, string>;
  /** document_fields.id -> name, for show-when-Yes details boxes (#337). */
  fieldNames?: Record<string, string>;
};

export type ParentAttachment = { id: string; field: 'program' | 'curriculum'; file_name: string; size_bytes: number; mime_type: string };

export const parentDocuments = {
  list: () => call<{ documents: ParentDocumentSummary[] }>('/api/parent?action=list').then(r => r.documents),
  get: (id: string) => call<ParentDocumentPayload>(`/api/parent?action=get&id=${encodeURIComponent(id)}`),
  markViewed: (documentId: string) => post<{ viewedAt: string | null }>('/api/parent', { action: 'mark-viewed', documentId }),
  sign: (documentId: string, signature: string, signedAt: string, contentHash: string) =>
    post<{ ok: true }>('/api/parent', { action: 'sign', documentId, signature, signedAt, contentHash }),
  attachments: (documentId: string) =>
    call<{ attachments: ParentAttachment[] }>(`/api/parent?action=attachments&id=${encodeURIComponent(documentId)}`).then(r => r.attachments),
  attachmentUrl: (attachmentId: string) =>
    call<{ url: string }>(`/api/parent?action=attachment-url&id=${encodeURIComponent(attachmentId)}`).then(r => r.url),
  submitAmendments: (documentId: string, amendments: Record<string, string>) =>
    post<{ ok: true }>('/api/parent', { action: 'submit-amendments', documentId, amendments }),
  /** The family's optional reflection (#392). An empty string clears it. */
  saveReflection: (documentId: string, text: string) =>
    post<{ ok: true }>('/api/parent', { action: 'save-reflection', documentId, text }),
};
